import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { SurveyResponse, AnalyticsMetrics } from '@/types';
import { getTrendData, getRatingDistribution } from '@/services/analytics.service';
import { useFiltersStore } from '@/store/useFiltersStore';

interface AnalyticsChartsProps {
  metrics: AnalyticsMetrics;
  responses: SurveyResponse[];
}

const AnalyticsCharts = ({ metrics }: AnalyticsChartsProps) => {
  const { filters } = useFiltersStore();
  const [trendData, setTrendData] = useState<Array<{ date: string; value: number; label: string }>>([]);
  const [distributionData, setDistributionData] = useState<Array<{ rating: number; count: number; percentage: number }>>([]);

  useEffect(() => {
    const loadChartData = async () => {
      const [trend, distribution] = await Promise.all([
        getTrendData(filters, 'day'),
        getRatingDistribution(filters, 'overall_overallSatisfied'),
      ]);
      setTrendData(trend);
      setDistributionData(distribution);
    };
    loadChartData();
  }, [filters]);

  const categoryData = [
    { name: 'Product Quality', value: metrics.categoryAverages.productQuality },
    { name: 'Delivery', value: metrics.categoryAverages.delivery },
    { name: 'Communication', value: metrics.categoryAverages.communication },
    { name: 'Staff', value: metrics.categoryAverages.staff },
    { name: 'Price', value: metrics.categoryAverages.price },
    { name: 'Overall', value: metrics.categoryAverages.overall },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Category Averages
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={categoryData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, 5]} />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#f97316" name="Average Rating" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Overall Satisfaction Trend
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis domain={[0, 5]} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#f97316"
              name="Average Rating"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white shadow rounded-lg p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Rating Distribution
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={distributionData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="rating" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#10b981" name="Count" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyticsCharts;

